import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import SearchInput from "@/components/ui/SearchInput";
import InfoTooltip from "@/components/ui/InfoTooltip";
import { useDashboard } from '@/contexts/DashboardContext';
import { fetchAgentPerformanceTable } from '@/lib/axios/api/widgets.api';
import { WidgetsTableRow } from "@/lib/axios/types/widgets.type";
import { cn } from "@/lib/utils";
import { Portal } from "@radix-ui/react-tooltip";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Download, Info } from "lucide-react";
import React, { useMemo, useState } from "react";
import * as XLSX from 'xlsx';

const exportToExcel = async (
  startDate: string,
  endDate: string,
  agentMicrosoftIds: string[] | null,
  agentSearchText: string,
  sortKey: string,
  sortDirection: 'asc' | 'desc',
  setExportLoading: (loading: boolean) => void
) => {
  try {
    setExportLoading(true);
    const result = await fetchAgentPerformanceTable({
      startTime: startDate,
      endTime: endDate,
      agentMicrosoftIds: agentMicrosoftIds ?? null,
      agentNameSearchText: agentSearchText || null,
      sortTable: { columnName: sortKey, columnValue: sortDirection },
      page: 1,
      limit: null, // Get all data
    });

    const data = result.data;
    const headers = [
      "Agent Name",
      "Inbound Handled",
      "Inbound Missed",
      "Outbound Handled",
      "Outbound Missed",
      "Direct Handled",
      "Direct Missed",
      "Transferred Calls",
      "Internal Joined",
      "Voice mail",
      "AHT",
      "AWT",
      "ASA"
    ];

    const rows = data.map(row => {
      return [
        row.agentName ?? '',
        Number(row.totalInboundCallsHandled ?? 0),
        Number(row.totalInboundCallsMissed ?? row.totalMissedCalls ?? 0),
        Number(row.totalOutboundCallsHandled ?? 0),
        Number(row.totalOutboundCallsMissed ?? 0),
        Number(row.totalInboundDirectCallsHandled ?? 0),
        Number(row.totalInboundDirectCallsMissed ?? row.totalMissedDirectCalls ?? 0),
        Number((row as unknown as { totalTransferredCalls?: number }).totalTransferredCalls ?? row.totalTransferred ?? 0),
        Number((row as unknown as { totalInternalCalls?: number }).totalInternalCalls ?? row.totalInternal ?? 0),
        Number((row as unknown as { totalVoicemailCalls?: number }).totalVoicemailCalls ?? 0),
        formatDuration(row.averageHandledTime),
        formatDuration((row as unknown as { averageWaitingTime?: number }).averageWaitingTime ?? 0),
        formatDuration((row as unknown as { averageSpeedOfAnswer?: number }).averageSpeedOfAnswer ?? 0),
      ];
    });

    const wb = XLSX.utils.book_new();
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Set column widths
    const colWidths = [
      { wch: 28 }, // Agent Name
      { wch: 18 }, // Inbound handled
      { wch: 18 }, // Inbound missed
      { wch: 18 }, // Outbound handled
      { wch: 18 }, // Outbound missed
      { wch: 18 }, // Direct handled
      { wch: 18 }, // Direct missed
      { wch: 20 }, // Transferred calls
      { wch: 18 }, // Internal joined
      { wch: 14 }, // Voicemail
      { wch: 16 }, // AHT
      { wch: 16 }, // AWT
      { wch: 16 }, // ASA
    ];
    ws['!cols'] = colWidths as unknown as XLSX.ColInfo[];

    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];

    XLSX.utils.book_append_sheet(wb, ws, "Agent Performance");
    XLSX.writeFile(wb, `agentperformance ${dateStr}.xlsx`);
  } catch (error) {
    console.error('Error exporting to Excel:', error);
  } finally {
    setExportLoading(false);
  }
};

const formatDuration = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '00:00:00';
  const totalSeconds = Math.round(Number(value));

  if (isNaN(totalSeconds) || totalSeconds < 0) {
    return '00:00:00';
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (num: number) => String(num).padStart(2, '0');

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const columns = [
  { title: "Agent Name", key: "agentName", width: 200 },
  { title: "Inbound Handled", key: "totalInboundCallsHandled", width: 160 },
  { title: "Inbound Missed", key: "totalInboundCallsMissed", width: 160 },
  { title: "Outbound Handled", key: "totalOutboundCallsHandled", width: 160 },
  { title: "Outbound Missed", key: "totalOutboundCallsMissed", width: 160 },
  { title: "Direct Handled", key: "totalInboundDirectCallsHandled", width: 160 },
  { title: "Direct Missed", key: "totalInboundDirectCallsMissed", width: 160 },
  { title: "Transferred Calls", key: "totalTransferredCalls", width: 160 },
  { title: "Internal Joined", key: "totalInternalCalls", width: 160 },
  { title: "Voice mail", key: "totalVoicemailCalls", width: 140 },
  { title: "AHT", key: "averageHandledTime", width: 140 },
  { title: "AWT", key: "averageWaitingTime", width: 140 },
  { title: "ASA", key: "averageSpeedOfAnswer", width: 140 },
];

const AgentPerformanceTableWidget: React.FC<{
  startDate?: string;
  endDate?: string;
  agentMicrosoftIds?: string[] | null;
}> = ({ startDate, endDate, agentMicrosoftIds = null }) => {
  const [sortKey, setSortKey] = useState<string>("agentName");
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>("asc");
  const [agentSearchText, setAgentSearchText] = useState("");
  const { theme } = useDashboard();
  const [tableData, setTableData] = useState<WidgetsTableRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const lastQueryKeyRef = React.useRef<string>("");
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const filteredData = useMemo(() => {
    return tableData;
  }, [tableData]);

  const sortedData = useMemo(() => {
    return filteredData;
  }, [filteredData]);

  // Add new function to calculate max values for gauge bars
  const maxValues = useMemo(() => {
    if (!tableData || tableData.length === 0) return {};

    const result = {};
    columns.forEach(col => {
      if (col.key !== 'agentName' && col.key !== 'callQueue') {
        const values = tableData.map(item => Number(item[col.key] || 0)).filter(val => !isNaN(val));
        result[col.key] = values.length > 0 ? Math.max(...values) : 0;
      }
    });
    return result;
  }, [tableData]);

  const calculateProgressPercentage = (value: number, maxValue: number): number => {
    if (maxValue === 0 || value === 0) return 0;
    return Math.min((value / maxValue) * 100, 100);
  };

  const getProgressBarColor = (key: string, value: number, maxValue: number): string => {
    if (key === 'sla') {
      if (value >= 90) return "bg-green-500";
      if (value >= 80) return "bg-amber-500";
      return "bg-red-500";
    }
    // All progress bars other than SLA use a dark blue color
    return theme === "dark" ? "bg-[#1681F6]" : "bg-[#0C3451]";
  };

  const getCellContent = (key: string, value: string | number | null | undefined) => {
    if (key === 'agentName') {
      return (
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <div className="text-left truncate max-w-[180px] cursor-pointer text-xs text-teamscx-primary dark:text-white">
                {value || '0'}
              </div>
            </TooltipTrigger>
            <Portal>
              <TooltipContent side="top" sideOffset={8} className="z-[99999]">
                <p className="text-xs text-teamscx-primary dark:text-white">{value || '0'}</p>
              </TooltipContent>
            </Portal>
          </Tooltip>
        </TooltipProvider>
      );
    }

    const numValue = Number(value || 0);
    const maxValue = maxValues[key] || 0;
    const progressPercentage = calculateProgressPercentage(numValue, maxValue);
    const isTime = ['averageHandledTime', 'averageWaitingTime', 'averageSpeedOfAnswer'].includes(key);
    const displayValue = isTime ? formatDuration(value) : numValue;

    return (
      <div className="flex items-center gap-2">
        <div className="w-[50px] h-[10px] bg-gray-200 rounded-md overflow-hidden">
          {numValue > 0 && (
            <div
              className={cn(
                "h-full",
                getProgressBarColor(key, numValue, maxValue)
              )}
              style={{ width: `${progressPercentage}%` }}
            />
          )}
        </div>
        <span>
          {displayValue}
        </span>
      </div>
    );
  };

  React.useEffect(() => {
    const canSearch = Boolean(startDate && endDate);
    const columnName = sortKey;
    const columnValue = sortDirection;
    const search = agentSearchText.trim();
    const agentIdsKey = (agentMicrosoftIds || []).join(',');
    const queryKey = `${startDate}|${endDate}|${agentIdsKey}|${search}|${columnName}|${columnValue}|${page}|${limit}`;
    if (!canSearch) {
      setTableData([]);
      lastQueryKeyRef.current = "";
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      // avoid duplicate calls for same params
      if (queryKey === lastQueryKeyRef.current) return;
      lastQueryKeyRef.current = queryKey;
      setLoading(true);
      setError("");
      try {
        const result = await fetchAgentPerformanceTable({
          startTime: startDate!,
          endTime: endDate!,
          agentMicrosoftIds: agentMicrosoftIds ?? null,
          agentNameSearchText: search || null,
          sortTable: columnName ? { columnName, columnValue } : undefined,
          page,
          limit,
        }, { signal: controller.signal });
        setTableData(result.data);
        setTotal(result.total);
      } catch (e) {
        if ((e as any)?.name === 'CanceledError' || (e as any)?.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'An error occurred');
        setTableData([]);
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [agentSearchText, sortKey, sortDirection, startDate, endDate, agentMicrosoftIds, page, limit]);

  // Reset page when search text or sort changes
  React.useEffect(() => {
    setPage(1);
  }, [agentSearchText, sortKey, sortDirection]);

  return (
    <Card className="min-h-[300px] dark:border-[#0C3451]">
      <CardHeader className="flex flex-row items-center justify-between sticky top-0 z-30 border-b">
        <div className="flex items-center gap-2">
          <span> Agent Performance</span>
          <InfoTooltip
            content="A list of all the agents, showing their detailed activities such as the number of calls handled, number of call transferred, number of call missed, number of internal calls joined, average handled time, average waiting time, average speed of answer."
          />
        </div>
        <div className="flex items-center gap-2">
          <SearchInput
            placeholder="Search agent..."
            onSearch={setAgentSearchText}
            initialValue={agentSearchText}
            debounceMs={500}
          />
          <Button
            variant="outline"
            className="h-8 text-xs flex items-center gap-1 [text-decoration:none] hover:bg-[#00FEC1] hover:border-[#00FEC1] hover:text-black transition-colors dark:bg-[#3C5C73] dark:border-[#4A6B85] dark:text-white dark:hover:bg-[#00FEC1] dark:hover:border-[#00FEC1] dark:hover:text-black"
            onClick={async (e) => {
              e.stopPropagation();
              if (startDate && endDate) {
                await exportToExcel(
                  startDate,
                  endDate,
                  agentMicrosoftIds,
                  agentSearchText,
                  sortKey,
                  sortDirection,
                  setExportLoading
                );
              }
            }}
            disabled={!startDate || !endDate || exportLoading}
          >
            {exportLoading ? (
              <>
                <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-3 w-3" />
                Export
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="relative overflow-hidden">
          <div
            className={cn(
              "w-full overflow-x-auto h-[285px] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 [&::-webkit-scrollbar-corner]:bg-transparent",
              !loading && sortedData.length > 0 ? "overflow-y-auto" : "overflow-y-hidden"
            )}
          >
            <table className="w-full table-fixed caption-bottom text-xs">
              <thead className="sticky top-0 z-20 bg-background border-b">
                <tr className="border-b transition-colors data-[state=selected]:bg-muted">
                  {columns.map((col, colIdx) => (
                    <th
                      key={col.key}
                      className={cn(
                        "h-[45px] px-4 text-left align-middle font-medium cursor-pointer bg-gray-50 dark:bg-[#3C5C73]",
                        colIdx === 0 && "sticky left-0 z-20"
                      )}
                      style={{ width: col.width }}
                    >
                      <div className="flex items-center">
                        <div
                          className="flex items-center gap-1 cursor-pointer"
                          onClick={() => handleSort(col.key)}
                        >
                          <span className="whitespace-nowrap truncate text-xs text-teamscx-primary dark:text-white font-medium">
                            {col.title}
                          </span>
                          {sortKey === col.key && (
                            sortDirection === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                          )}
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {loading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={idx} className="transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted h-[45px]">
                      {columns.map((col, colIdx) => (
                        <td
                          key={col.key}
                          className={cn(
                            "p-4 align-middle",
                            colIdx === 0 && "sticky left-0 z-18 bg-gray-50 dark:bg-[#3C5C73]"
                          )}
                          style={{ width: col.width }}
                        >
                          <div className="h-4 w-full bg-gray-200 animate-pulse rounded" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : sortedData.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="p-0" style={{ height: "285px" }}>
                      <div className="flex items-center justify-center h-[285px] text-sm text-muted-foreground dark:text-white/60">
                        <span>No call records were found</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  sortedData.map((row, idx) => (
                    <tr
                      key={`${row.agentName || 'unknown'}-${idx}`}
                      className={cn(
                        "transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted h-[45px]",
                        idx % 2 === 0 ? "bg-white dark:bg-[#0C3451]" : "bg-gray-50 dark:bg-[#3C5C73]"
                      )}
                    >
                      {columns.map((col, colIdx) => (
                        <td
                          key={col.key}
                          className={cn(
                            "p-4 align-middle",
                            colIdx === 0 && cn(
                              "sticky left-0 z-18",
                              idx % 2 === 0 ? "bg-white dark:bg-[#0C3451]" : "bg-gray-50 dark:bg-[#3C5C73]"
                            )
                          )}
                          style={{ width: col.width }}
                        >
                          <div className="text-left truncate max-w-[180px] cursor-pointer text-xs">
                            {getCellContent(col.key, row[col.key] || '0')}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex items-center justify-between p-3 pt-2 border-t dark:border-white dark:bg-[#3C5C73]">
          <div className="text-xs text-muted-foreground">
            {(() => {
              const totalItems = total;
              const startIndex = (page - 1) * limit + 1;
              const endIndex = Math.min(page * limit, totalItems);
              return `Showing ${startIndex}-${endIndex} of ${totalItems}`;
            })()}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="w-8 h-8 grid place-items-center rounded border disabled:opacity-50"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs px-1">{page} / {(() => {
              const totalItems = total;
              return Math.max(1, Math.ceil(totalItems / limit));
            })()}</span>
            <button
              className="w-8 h-8 grid place-items-center rounded border disabled:opacity-50"
              onClick={() => setPage((p) => p + 1)}
              disabled={(() => {
                const totalItems = total;
                const totalPages = Math.max(1, Math.ceil(totalItems / limit));
                return page >= totalPages || loading;
              })()}
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AgentPerformanceTableWidget;
